import {
  TREATMENT_RUNTIME_POINTER_SCHEMA_VERSION,
  TREATMENT_STORAGE_KEY,
} from "./treatmentApiConstants";
import { normalizeRuntimeApiBaseUrl } from "./runtimeApiBase";

const resumeError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  error.action = { code: "start_over", label: "Start over" };
  return error;
};

const parse = (key) => {
  const raw = sessionStorage.getItem(key);
  if (raw == null) return null;
  try {
    return JSON.parse(raw);
  } catch (_error) {
    throw resumeError(
      "runtime_pointer_invalid",
      "Saved preview progress is invalid. Start over to continue.",
    );
  }
};

export const loadTreatmentPreviewSession = (
  capabilityToken,
  expected = {},
) => {
  if (!capabilityToken) return null;
  let saved = parse(TREATMENT_STORAGE_KEY.runtimePointer);
  if (!saved) {
    const legacy = parse(TREATMENT_STORAGE_KEY.previewSession);
    if (!legacy) return null;
    if (legacy.capabilityToken !== capabilityToken || !legacy.sessionToken) {
      throw resumeError(
        "launch_contract_conflict",
        "Saved preview progress belongs to a different preview.",
      );
    }
    saved = {
      schema_version: TREATMENT_RUNTIME_POINTER_SCHEMA_VERSION,
      mode: "preview",
      runtime_api_base_url: normalizeRuntimeApiBaseUrl(
        expected.apiBaseUrl || legacy.apiBaseUrl,
      ),
      source_kind: expected.sourceKind || null,
      source_id: expected.sourceId || null,
      release_id: expected.snapshotId || null,
      release_version: null,
      release_checksum: expected.snapshotChecksum || null,
      session_token: legacy.sessionToken,
      last_known_revision: legacy.sessionRevision ?? null,
      current_node_id: legacy.currentItemId || null,
      patient_ref: null,
    };
  }
  if (
    saved.schema_version !== TREATMENT_RUNTIME_POINTER_SCHEMA_VERSION
    || saved.mode !== "preview"
    || !saved.session_token
  ) {
    throw resumeError(
      "runtime_pointer_invalid",
      "Saved preview progress is invalid. Start over to continue.",
    );
  }
  const expectedApiBaseUrl = expected.apiBaseUrl
    ? normalizeRuntimeApiBaseUrl(expected.apiBaseUrl)
    : null;
  if (
    (expectedApiBaseUrl
      && saved.runtime_api_base_url !== expectedApiBaseUrl)
    || (expected.snapshotId
      && saved.release_id
      && String(saved.release_id) !== String(expected.snapshotId))
    || (expected.snapshotChecksum
      && saved.release_checksum
      && String(saved.release_checksum) !== String(expected.snapshotChecksum))
  ) {
    throw resumeError(
      "launch_contract_conflict",
      "Saved preview progress belongs to a different preview snapshot.",
    );
  }
  return saved;
};

export const persistTreatmentPreviewSession = (
  capabilityToken,
  session,
  patch = {},
) => {
  if (!capabilityToken || !session?.session_token) return null;
  const existing = (() => {
    try {
      return loadTreatmentPreviewSession(capabilityToken) || {};
    } catch (_error) {
      return {};
    }
  })();
  const release = session.release || {};
  const pointer = {
    schema_version: TREATMENT_RUNTIME_POINTER_SCHEMA_VERSION,
    mode: "preview",
    runtime_api_base_url: normalizeRuntimeApiBaseUrl(
      session.preview_api_base_url || existing.runtime_api_base_url,
    ),
    source_kind: release.source_kind || existing.source_kind || null,
    source_id: release.source_id || existing.source_id || null,
    release_id: (
      release.snapshot_id || release.id || existing.release_id || null
    ),
    release_version: release.version ?? existing.release_version ?? null,
    release_checksum: release.checksum || existing.release_checksum || null,
    session_token: session.session_token,
    last_known_revision: session.session_revision,
    current_node_id: patch.currentItemId ?? existing.current_node_id ?? null,
    patient_ref: null,
  };
  const lastViewedConsentNodeId = patch.lastViewedConsentNodeId !== undefined
    ? patch.lastViewedConsentNodeId
    : existing.last_viewed_consent_node_id;
  if (lastViewedConsentNodeId !== undefined) {
    pointer.last_viewed_consent_node_id = lastViewedConsentNodeId;
  }
  sessionStorage.setItem(
    TREATMENT_STORAGE_KEY.runtimePointer,
    JSON.stringify(pointer),
  );
  sessionStorage.removeItem(TREATMENT_STORAGE_KEY.previewSession);
  return pointer;
};

export const clearTreatmentPreviewSession = () => {
  sessionStorage.removeItem(TREATMENT_STORAGE_KEY.runtimePointer);
  sessionStorage.removeItem(TREATMENT_STORAGE_KEY.previewSession);
};
